import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ImpersonationBanner from './ImpersonationBanner';
import {
  LayoutDashboard,
  Users,
  TrendingUp,
  BookOpen,
  Building2,
  UserPlus,
  User,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Shield,
  Ticket,
  FileSpreadsheet,
  ClipboardList,
  Trophy,
  Megaphone,
  LifeBuoy,
  Rocket,
  Target
} from 'lucide-react';
import { PrivacyToggle } from './PrivacyToggle';
import { AnimatePresence, motion } from 'framer-motion';

const NAVY = '#0B1437';

export default function Layout({ children }) {
  const { user, logout, impersonatedUser, stopImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Support & Questions', href: '/support', icon: LifeBuoy, gold: true },
    { name: 'Buy Breeze Leads', href: '/leads', icon: Target, cyan: true },
    { name: 'My Team', href: '/team', icon: Users },
    { name: 'Production', href: '/production', icon: TrendingUp },
    { name: 'Tracker', href: '/tracker', icon: ClipboardList },
    { name: 'Promotions & Bonuses', href: '/promotions', icon: Trophy },
    { name: 'Book of Business', href: '/book-of-business', icon: BookOpen },
    { name: 'Resources', href: '/resources', icon: BookOpen },
    { name: 'Carriers', href: '/carriers', icon: Building2 },
    { name: 'Recruiting', href: '/recruiting', icon: UserPlus },
    { name: 'Tickets', href: '/my-tickets', icon: Ticket },
    { name: 'Profile', href: '/profile', icon: User },
  ];

  // New-agent gate: until the onboarding checklist is complete, only show the allowed sections
  const isGated = user?.role !== 'admin' && user?.onboarding_complete === false;
  const gatedNavigation = [
    { name: 'Get Started', href: '/onboarding', icon: Rocket, gold: true },
    { name: 'My Team', href: '/team', icon: Users },
    { name: 'Support & Questions', href: '/support', icon: LifeBuoy, gold: true },
    { name: 'Recruiting', href: '/recruiting', icon: UserPlus },
    { name: 'Profile', href: '/profile', icon: User },
  ];
  const effectiveNavigation = isGated ? gatedNavigation : navigation;

  const adminNavigation = user?.role === 'admin'
    ? [
        { name: 'Manage Tickets', href: '/admin-tickets', icon: Ticket },
        { name: 'Onboarding Tracker', href: '/admin/onboarding-tracker', icon: ClipboardList },
        { name: 'Email Blast', href: '/email-blast', icon: Megaphone },
        { name: 'CSV Import', href: '/csv-import', icon: FileSpreadsheet },
        { name: 'Audit Log', href: '/audit-log', icon: Shield }
      ]
    : [];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = (user?.name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.href;
    const Icon = item.icon;
    return (
      <Link to={item.href} onClick={() => setSidebarOpen(false)}>
        <div
          data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
          className={`group relative flex items-center gap-3 rounded-xl px-3 py-2 transition-colors duration-200 ${
            isActive
              ? item.gold
                ? 'bg-gradient-to-r from-[#D89E2F] to-[#B47E14] shadow-[0_8px_20px_-6px_rgba(216,158,47,0.55)]'
                : item.cyan
                  ? 'bg-gradient-to-r from-[#00E5FF] via-[#23ACD5] to-[#2447D6] shadow-[0_10px_24px_-6px_rgba(0,229,255,0.6)]'
                  : 'bg-gradient-to-r from-[#2447D6] to-[#1E3FBF] shadow-[0_8px_20px_-6px_rgba(36,71,214,0.55)]'
              : item.cyan
                ? 'overflow-hidden border border-[#00E5FF]/30 bg-gradient-to-r from-[#00E5FF]/15 via-[#23ACD5]/10 to-transparent shadow-[0_0_18px_-4px_rgba(0,229,255,0.45)] hover:from-[#00E5FF]/25 hover:via-[#23ACD5]/15 hover:shadow-[0_0_24px_-4px_rgba(0,229,255,0.7)]'
                : 'hover:bg-white/[0.06]'
          }`}
        >
          {item.cyan && !isActive && <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(110deg,transparent_30%,rgba(0,229,255,0.2)_50%,transparent_70%)] bg-[length:220%_100%] animate-[nav-shimmer_3.2s_ease-in-out_infinite]" />}
          <div
            className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] transition-transform duration-200 group-hover:scale-105 ${
              isActive
                ? 'bg-white/20 backdrop-blur-sm'
                : item.gold
                  ? 'bg-gradient-to-br from-[#F6C561] to-[#D89E2F] shadow-[0_4px_10px_-2px_rgba(216,158,47,0.5)]'
                  : item.cyan
                    ? 'bg-gradient-to-br from-[#00E5FF] to-[#23ACD5] shadow-[0_4px_14px_-2px_rgba(0,229,255,0.75)]'
                    : 'bg-gradient-to-br from-[#3B82F6] to-[#2563EB] shadow-[0_4px_10px_-2px_rgba(37,99,235,0.5)]'
            }`}
          >
            <Icon className={`h-[18px] w-[18px] ${item.cyan && !isActive ? 'text-[#06263A]' : 'text-white'}`} strokeWidth={item.cyan ? 2.4 : 2} />
          </div>
          <span
            className={`relative flex-1 text-[13.5px] font-semibold tracking-wide ${
              isActive
                ? 'text-white'
                : item.gold
                  ? 'text-[#F6C561] group-hover:text-[#FFD98A]'
                  : item.cyan
                    ? 'font-extrabold text-[#7FF3FF] group-hover:text-white'
                    : 'text-[#A3AED0] group-hover:text-white'
            }`}
          >
            {item.name}
          </span>
          <ChevronRight
            className={`h-4 w-4 text-white/50 transition-all duration-200 ${
              isActive ? 'opacity-100' : 'opacity-0 -translate-x-1 group-hover:opacity-60 group-hover:translate-x-0'
            }`}
          />
        </div>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#F4F7FE]">
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 z-40 flex h-screen w-[280px] flex-col transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: NAVY }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 pb-5 pt-7">
          <img
            src="/breeze-logo.png"
            alt="Breeze Financial Group"
            className="h-11 w-auto object-contain"
          />
          <div className="flex flex-col">
            <span className="font-['Manrope'] text-[22px] font-extrabold tracking-[0.14em] text-white leading-none">
              ATLAS
            </span>
            <span className="mt-1 text-[9px] font-semibold uppercase tracking-[0.28em] text-[#A3AED0]">
              Agency OS
            </span>
          </div>
          <button
            className="ml-auto rounded-lg p-1.5 text-[#A3AED0] hover:bg-white/10 hover:text-white lg:hidden"
            onClick={() => setSidebarOpen(false)}
            data-testid="close-sidebar-button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mx-6 h-px bg-white/[0.08]" />

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-5 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.15)_transparent]">
          {effectiveNavigation.map((item) => (
            <NavItem key={item.name} item={item} />
          ))}
          {adminNavigation.length > 0 && (
            <>
              <div className="px-3 pb-1 pt-5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#5D6B98]">
                Admin
              </div>
              {adminNavigation.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}
            </>
          )}
        </nav>

        {/* User card + logout */}
        <div className="px-4 pb-5 pt-2">
          <div
            className="flex cursor-pointer items-center gap-3 rounded-2xl bg-white/[0.05] p-3 transition-colors hover:bg-white/[0.09]"
            onClick={() => { setSidebarOpen(false); navigate('/profile'); }}
            data-testid="sidebar-user-card"
          >
            {user?.profile_picture ? (
              <img
                src={user.profile_picture}
                alt={user?.name}
                className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-white/20"
              />
            ) : (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#3B82F6] to-[#2563EB] text-sm font-bold text-white">
                {initials}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-white" data-testid="user-name">
                {user?.name}
              </p>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#A3AED0]">
                {user?.role}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-[#5D6B98]" />
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-2 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13.5px] font-semibold text-[#A3AED0] transition-colors hover:bg-white/[0.06] hover:text-white"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-white/[0.06]">
              <LogOut className="h-[18px] w-[18px]" strokeWidth={2} />
            </div>
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-[280px]">
        {impersonatedUser && (
          <ImpersonationBanner
            impersonatedUser={impersonatedUser}
            onStopImpersonation={async () => {
              await stopImpersonation();
              // Full reload so every page refetches data as the restored admin
              window.location.href = '/';
            }}
          />
        )}

        {/* Mobile top bar */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-[#E9EDF7] bg-white/90 px-4 backdrop-blur-xl lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            data-testid="toggle-sidebar-button"
            className="rounded-lg p-2 text-[#2B3674] hover:bg-[#F4F7FE]"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-['Manrope'] text-lg font-extrabold tracking-[0.12em] text-[#2B3674]">
            ATLAS
          </span>
          <PrivacyToggle className="ml-auto" />
        </header>

        {/* Desktop header bar — privacy toggle next to the user chip */}
        <div className="sticky top-0 z-30 hidden h-12 items-center justify-end gap-3 bg-[#F4F7FE]/80 px-8 backdrop-blur-md lg:flex" data-testid="desktop-header">
          <PrivacyToggle />
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 rounded-full border border-[#E9EDF7] bg-white/90 py-1 pl-1 pr-3 text-xs font-bold text-[#2B3674] shadow-sm transition-colors hover:bg-[#F4F7FE]"
            data-testid="header-user-chip"
          >
            {user?.profile_picture ? (
              <img src={user.profile_picture} alt={user?.name} className="h-6 w-6 rounded-full object-cover" />
            ) : (
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[#3B82F6] to-[#2563EB] text-[10px] text-white">{initials}</span>
            )}
            <span className="max-w-[140px] truncate">{user?.name}</span>
          </button>
        </div>

        <main className="p-4 sm:p-6 lg:px-8 lg:pb-8 lg:pt-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
